import { PageController } from '@client/components/_core/layout/PageController';
import { DocumentLibrary } from '@client/components/_shared/documents/DocumentLibrary';
import {
    isPushableClaimPhotoRow,
    RunRavinOnPhotosModal,
} from '@client/components/claims/vehicle-inspection/RunRavinOnPhotosModal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { isVehicleProductCategory } from '@shared/constants';
import { RAVIN_IMAGE_PUSH_LIMITS } from '@shared/constants/business/ravin-image-push';
import { createFileRoute } from '@tanstack/react-router';
import { Images } from 'lucide-react';
import { useEffect, useState } from 'react';
import { claimFormStore } from '../store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/library')({
    component: Library,
});

function Library() {
    const claim = claimFormStore.use.formData();
    // CUR-725: closed claims are read-only — uploads and file edits are withheld.
    const isReadonly = claimFormStore.use.isReadonly();
    const { trpc } = useTrpc();
    // The pending run: the ticked photo ids and how to untick them once it has been sent.
    const [run, setRun] = useState<{ fileIds: string[]; clearSelection: () => void } | null>(null);

    // CUR-1238: running Ravin on selected claim photos. The vehicle product
    // category and read-only state decide whether to ask at all; the server's
    // `canPushImages` (claim access, provider upload readiness) decides whether
    // the action shows.
    const isVehicleClaim = isVehicleProductCategory(claim.ProductType?.category);
    const gatesOpen = isVehicleClaim && !isReadonly;
    const inspectionsQuery = trpc.vehicleInspection.listForClaim.useQuery(
        { claimId: claim.id },
        { enabled: gatesOpen, retry: 1, staleTime: 60_000 },
    );
    // A disabled query keeps its last data, so the client gates are re-applied
    // here: closing the claim withdraws the action at once.
    const canPushImages = gatesOpen && (inspectionsQuery.data?.canPushImages ?? false);
    useEffect(() => {
        if (!canPushImages) setRun(null);
    }, [canPushImages]);

    return (
        <PageController route={Route}>
            <DocumentLibrary
                entity="claim"
                entityId={claim.id}
                disableUploading={isReadonly}
                readOnly={isReadonly}
                showDownloadAllButton
                fileNamePrefix={`${claim.generatedId || claim.id}`}
                shouldIncludeRelatedEntities
                selectionAction={
                    canPushImages
                        ? {
                              action: 'Run Ravin on',
                              noun: 'photos',
                              nounSingular: 'photo',
                              icon: <Images />,
                              maxSelections: RAVIN_IMAGE_PUSH_LIMITS.maxFiles,
                              // Only this claim's own JPEG/PNG files can be pushed —
                              // not related-entity, task, comment or deleted rows.
                              isSelectable: (row) => !row.isDeleted && isPushableClaimPhotoRow(row, claim.id),
                              unselectableHint: 'Only JPEG or PNG photos on this claim can be sent to Ravin',
                              onAction: (rows, { clearSelection }) =>
                                  setRun({ fileIds: rows.map((row) => row.id), clearSelection }),
                          }
                        : undefined
                }
            />
            {run && (
                <RunRavinOnPhotosModal
                    claimId={claim.id}
                    initialFileIds={run.fileIds}
                    onClose={(ran) => {
                        if (ran) run.clearSelection();
                        setRun(null);
                    }}
                />
            )}
        </PageController>
    );
}
